import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { canAccess, defaultLanding } from "@/lib/permissions";
import AppLayout from "@/components/app/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import Haulers from "@/pages/Haulers";
import Loads from "@/pages/Loads";
import Tracking from "@/pages/Tracking";
import Fleet from "@/pages/Fleet";
import Drivers from "@/pages/Drivers";
import Sites from "@/pages/Sites";
import BOLView from "@/pages/BOLView";
import AuditLog from "@/pages/AuditLog";
import UsersPage from "@/pages/Users";
import ExportPage from "@/pages/Export";
import HaulerInvoices from "@/pages/HaulerInvoices";
import HaulerInvoiceView from "@/pages/HaulerInvoiceView";
import Stockpiles from "@/pages/Stockpiles";
import Exceptions from "@/pages/Exceptions";
import HaulingSummary from "@/pages/HaulingSummary";
import VerificationReports from "@/pages/VerificationReports";
import RefDataAdmin from "@/pages/system/RefDataAdmin";
import SystemHealth from "@/pages/system/SystemHealth";
import Compliance from "@/pages/Compliance";
import Settings from "@/pages/Settings";
import Notifications from "@/pages/Notifications";
import AssistantWidget from "@/components/app/AssistantWidget";
import InstallPrompt from "@/components/app/InstallPrompt";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-gray-500 font-mono">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/**
 * Wraps a route element so it only renders if the current user's role can
 * access `path`. Falls back to the user's default landing page otherwise.
 * Wildcard-style paths (e.g. `/bol/:id`) use `permissionPath` to look up
 * the required role.
 */
function RoleGuard({ children, permissionPath }) {
  const { user } = useAuth();
  const location = useLocation();
  const path = permissionPath || location.pathname;
  if (!canAccess(user?.role, path)) {
    return <Navigate to={defaultLanding(user?.role)} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        element={
          <Protected>
            <AppLayout />
          </Protected>
        }
      >
        <Route path="/" element={<RoleGuard><Dashboard /></RoleGuard>} />
        <Route path="/projects" element={<RoleGuard><Projects /></RoleGuard>} />
        <Route path="/haulers" element={<RoleGuard><Haulers /></RoleGuard>} />
        <Route path="/loads" element={<RoleGuard><Loads /></RoleGuard>} />
        <Route path="/tracking" element={<RoleGuard><Tracking /></RoleGuard>} />
        <Route path="/fleet" element={<RoleGuard><Fleet /></RoleGuard>} />
        <Route path="/drivers" element={<RoleGuard><Drivers /></RoleGuard>} />
        <Route path="/sites" element={<RoleGuard><Sites /></RoleGuard>} />
        <Route path="/hauler-invoices" element={<RoleGuard><HaulerInvoices /></RoleGuard>} />
        <Route path="/stockpiles" element={<RoleGuard><Stockpiles /></RoleGuard>} />
        <Route path="/hauling-summary" element={<RoleGuard><HaulingSummary /></RoleGuard>} />
        <Route path="/verification" element={<RoleGuard><VerificationReports /></RoleGuard>} />
        <Route path="/system/container-types" element={<RoleGuard><RefDataAdmin kind="container-types" title="Container Types" subtitle="Truck / trailer types shown in the Load form" /></RoleGuard>} />
        <Route path="/system/material-types" element={<RoleGuard><RefDataAdmin kind="material-types" title="Material Types" subtitle="Soil / aggregate types shown in the Load & Stockpile forms" /></RoleGuard>} />
        <Route path="/system/health" element={<RoleGuard><SystemHealth /></RoleGuard>} />
        <Route path="/exceptions" element={<RoleGuard><Exceptions /></RoleGuard>} />
        <Route path="/compliance" element={<RoleGuard><Compliance /></RoleGuard>} />
        <Route path="/audit" element={<RoleGuard><AuditLog /></RoleGuard>} />
        <Route path="/users" element={<RoleGuard><UsersPage /></RoleGuard>} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/export" element={<RoleGuard><ExportPage /></RoleGuard>} />
      </Route>
      <Route path="/bol/:id" element={<Protected><RoleGuard permissionPath="/loads"><BOLView /></RoleGuard></Protected>} />
      <Route path="/hauler-invoices/:id" element={<Protected><RoleGuard permissionPath="/hauler-invoices"><HaulerInvoiceView /></RoleGuard></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <AssistantWidget />
        <InstallPrompt />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
